import { Question } from "@/data/questions";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Star } from "lucide-react";

interface QuestionCardProps {
  question: Question;
  selectedValue: number | null;
  onSelect: (value: number) => void;
  onContinue: () => void;
  canContinue: boolean;
}

const competenceAreaAr: Record<string, string> = {
  "Information & Data Literacy": "محو الأمية في المعلومات والبيانات",
  "Communication & Collaboration": "التواصل والتعاون",
  "Digital Content Creation": "إنشاء المحتوى الرقمي",
  "Safety": "السلامة والأمان",
  "Problem Solving": "حل المشكلات"
};

export const QuestionCard = ({ question, selectedValue, onSelect, onContinue, canContinue }: QuestionCardProps) => {
  const levels = 8; // 8-star rating system

  return (
    <div className="bg-[hsl(210,25%,25%)] rounded-lg shadow-2xl overflow-hidden border border-[hsl(210,25%,35%)]">
      {/* Competence Area Header */}
      <div className="bg-[hsl(210,25%,20%)] py-4 px-8 border-b border-[hsl(210,25%,35%)] flex items-center justify-center gap-4 md:gap-8">
        {/* Thumbnail Image */}
        <div className="h-16 w-24 md:h-20 md:w-32 rounded-md overflow-hidden bg-gray-800/50 border-2 border-gray-400 shrink-0 shadow-sm">
          <img
            src={question.image}
            alt={question.area}
            className="h-full w-full object-cover"
          />
        </div>

        <h2 className="text-xl md:text-2xl font-light tracking-widest text-gray-100 text-left uppercase flex flex-col gap-1">
          <span>{question.area}</span>
          <span className="font-tajawal text-lg md:text-xl text-gray-400">{competenceAreaAr[question.area] || ''}</span>
        </h2>
      </div>

      {/* Content */}
      <div className="p-4 md:p-12">
        {/* Image Moved to Header */}

        <p className="text-gray-300 text-center mb-6 text-sm md:text-base">
          Choose the proficiency level for the following competence that describes you best
        </p>

        {/* Question in Quote Style */}
        <div className="mb-6 relative space-y-4 md:space-y-6">
          {/* English Question */}
          <div className="relative">
            <div className="text-4xl md:text-6xl text-gray-400 absolute -top-3 -left-1 opacity-25">"</div>
            <blockquote className="pl-6 md:pl-8">
              <p className="text-lg md:text-3xl italic text-gray-100 font-light leading-relaxed">
                {question.question}
              </p>
            </blockquote>
          </div>

          {/* Arabic Question */}
          <div className="relative" dir="rtl">
            <div className="text-4xl md:text-6xl text-gray-400 absolute -top-3 -right-1 opacity-25">"</div>
            <blockquote className="pr-6 md:pr-8">
              <p className="text-lg md:text-3xl italic text-gray-100/90 font-tajawal font-light leading-relaxed">
                {question.questionAr}
              </p>
            </blockquote>
          </div>
        </div>

        <div className="border-t border-b border-[hsl(210,25%,35%)] py-6 md:py-8 my-6 md:my-8 px-1">
          {/* Level Labels */}
          <div className="flex justify-between mb-4 px-2">
            <span className="text-xs md:text-sm text-gray-400">Low level</span>
            <span className="text-xs md:text-sm text-gray-400">High level</span>
          </div>

          {/* Star Rating */}
          <div className="flex justify-center gap-1.5 md:gap-3">
            {Array.from({ length: levels }, (_, i) => i + 1).map((level) => (
              <button
                key={level}
                onClick={() => onSelect(level)}
                className={cn(
                  "transition-all duration-300 hover:scale-110 shrink-0",
                  "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-[hsl(210,25%,25%)]"
                )}
                aria-label={`Rate level ${level} out of ${levels}`}
              >
                <Star
                  className={cn(
                    "w-7 h-7 md:w-10 md:h-10 transition-all duration-300",
                    selectedValue && selectedValue >= level
                      ? "fill-gray-100 text-gray-100"
                      : "fill-transparent text-gray-400 hover:text-gray-300"
                  )}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Continue Button */}
        <div className="flex justify-center mt-6 md:mt-8">

          <Button
            onClick={onContinue}
            disabled={!canContinue}
            size="lg"
            className={cn(
              "w-full md:w-auto px-8 py-5 md:py-6 text-sm md:text-base tracking-wide uppercase rounded-md",
              "bg-transparent border-2 border-gray-400 text-gray-200",
              "hover:bg-gray-700 hover:border-gray-300",
              "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            )}
          >
            Continue →
          </Button>
        </div>
      </div>
    </div>
  );
};
